import {
  Button,
  FormHelperText,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core"
import { useContext, useEffect } from "react"
import { useQuery } from "react-query"
import { useDebounce } from "use-lodash-debounce"
import { getLesson } from "~/actions/lessons"
import AppContext from "~/contexts/App"
import LessonContext from "~/contexts/Lesson"

import Box from "~/components/Common/Box"
import useStyles from "./styles"

const LessonSettings = () => {
  const classes = useStyles()

  const {
    setDebouncedDetails,
    lesson,
    lessonDetailsValues,
    setLessonDetailsValues,
    setInitialDetailsContext,
    unsavedState,
    tryPublish,
  } = useContext(LessonContext)

  const query = useQuery(["lesson", lesson.id], () => getLesson(lesson.id))
  useEffect(() => {
    query.refetch()
  }, [])

  const { setPageSubTitle } = useContext(AppContext)
  const debouncedLocalValues = useDebounce(lessonDetailsValues, 300)

  //set lesson subtitle to empty string
  useEffect(() => {
    setPageSubTitle("")
  }, [])

  //sets lesson details to context
  useEffect(() => {
    if (lesson?.id) {
      if (unsavedState.length === 0) {
        setLessonDetailsValues({
          name: lesson?.name,
          description: lesson?.description,
          date: lesson?.date,
          theater_view: lesson?.theater_view,
          show_lab: lesson?.show_lab,
          skippable: lesson?.skippable,
          created_by: lesson?.created_by || null,
          vaas_provider_name: lesson?.vaas_provider_name || "",
          vaas_system_version: lesson?.vaas_system_version || "",
          vaas_creation_timestamp: lesson?.vaas_creation_timestamp
            ? lesson.vaas_creation_timestamp.slice(0, 16)
            : "",
        })
        setInitialDetailsContext({
          name: lesson.name,
          description: lesson.description || "",
          theater_view: lesson?.theater_view,
          show_lab: lesson?.show_lab,
          skippable: lesson?.skippable,
          created_by: lesson?.created_by || null,
          vaas_provider_name: lesson?.vaas_provider_name || "",
          vaas_system_version: lesson?.vaas_system_version || "",
          vaas_creation_timestamp: lesson?.vaas_creation_timestamp
            ? lesson.vaas_creation_timestamp.slice(0, 16)
            : "",
        })
      }
    }
  }, [lesson])
  useEffect(() => {
    setDebouncedDetails(lessonDetailsValues)
  }, [debouncedLocalValues])

  return (
    <Box className={classes.container}>
      <Typography variant="h3" color="primary" className={classes.title}>
        Lesson Settings
      </Typography>
      <Box display="flex">
        <Typography color="secondary" className={classes.inputLabel}>
          Theatre Mode
        </Typography>
        <Switch
          onChange={(e) => {
            setLessonDetailsValues((oldState) => ({
              ...oldState,
              theater_view: e.target.checked,
            }))
          }}
          value={Boolean(lessonDetailsValues?.theater_view)}
          checked={Boolean(lessonDetailsValues?.theater_view)}
        />
      </Box>
      <Box display="flex">
        <Typography color="secondary" className={classes.inputLabel}>
          Show Lab
        </Typography>
        <Switch
          onChange={(e) => {
            setLessonDetailsValues((oldState) => ({
              ...oldState,
              show_lab: e.target.checked,
            }))
          }}
          value={Boolean(lessonDetailsValues?.show_lab)}
          checked={Boolean(lessonDetailsValues?.show_lab)}
        />
      </Box>
      <Box display="flex">
        <Typography color="secondary" className={classes.inputLabel}>
          Skippable
        </Typography>
        <Switch
          onChange={(e) => {
            setLessonDetailsValues((oldState) => ({
              ...oldState,
              skippable: e.target.checked,
            }))
          }}
          value={Boolean(lessonDetailsValues?.skippable)}
          checked={Boolean(lessonDetailsValues?.skippable)}
          disabled={Boolean(!lesson.can_toggle_skippable)}
        />
      </Box>
      {!lesson.can_toggle_skippable && (
        <p>
          <em>
            Lessons with Knowledge Checks or Sentiment Questions cannot be
            skipped.
          </em>
        </p>
      )}
      <Typography
        variant="h3"
        color="primary"
        className={classes.title}
        style={{ marginTop: "24px" }}
      >
        AI-Generated Metadata
      </Typography>
      <Box>
        <Typography color="secondary" className={classes.inputLabel}>
          Created By
        </Typography>
        <select
          value={lessonDetailsValues?.created_by ?? ""}
          onChange={(e) => {
            const value = e.target.value === "" ? null : e.target.value
            const isAi = value === "modified-ai" || value === "created-ai"
            setLessonDetailsValues((oldState) => ({
              ...oldState,
              created_by: value,
              vaas_provider_name: isAi ? oldState.vaas_provider_name : "",
              vaas_system_version: isAi ? oldState.vaas_system_version : "",
              vaas_creation_timestamp: isAi
                ? oldState.vaas_creation_timestamp
                : "",
            }))
          }}
          className="border border-gray1 rounded-lg px-4 h-8 w-full bg-gray1 text-[12px] triangle-down-select-icon mr-2"
        >
          <option value="">None</option>
          <option value="human">Human</option>
          <option value="modified-ai">Modified AI</option>
          <option value="created-ai">Created AI</option>
        </select>
        {tryPublish && !lessonDetailsValues?.created_by && (
          <FormHelperText error>Created By is required to publish</FormHelperText>
        )}
      </Box>
      {(lessonDetailsValues?.created_by === "modified-ai" ||
        lessonDetailsValues?.created_by === "created-ai") && (
        <>
          <Box marginTop="8px">
            <Typography color="secondary" className={classes.inputLabel}>
              Provider Name
            </Typography>
            <TextField
              placeholder='e.g., "OpenAI"'
              value={lessonDetailsValues?.vaas_provider_name || ""}
              onChange={(e) => {
                setLessonDetailsValues((oldState) => ({
                  ...oldState,
                  vaas_provider_name: e.target.value,
                }))
              }}
              style={{ marginBottom: "10px" }}
              InputProps={{
                classes: {
                  root: classes.inputBackgroundColor,
                },
              }}
              variant="filled"
              size="small"
              fullWidth
            />
          </Box>
          <Box marginTop="8px">
            <Typography color="secondary" className={classes.inputLabel}>
              System Version
            </Typography>
            <TextField
              placeholder='e.g., "DALL-E 3.5"'
              value={lessonDetailsValues?.vaas_system_version || ""}
              onChange={(e) => {
                setLessonDetailsValues((oldState) => ({
                  ...oldState,
                  vaas_system_version: e.target.value,
                }))
              }}
              style={{ marginBottom: "10px" }}
              InputProps={{
                classes: {
                  root: classes.inputBackgroundColor,
                },
              }}
              variant="filled"
              size="small"
              fullWidth
            />
          </Box>
          <Box marginTop="8px">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography color="secondary" className={classes.inputLabel}>
                Creation Timestamp
              </Typography>
              {lessonDetailsValues?.vaas_creation_timestamp && (
                <Button
                  size="small"
                  onClick={() =>
                    setLessonDetailsValues((oldState) => ({
                      ...oldState,
                      vaas_creation_timestamp: "",
                    }))
                  }
                >
                  Clear
                </Button>
              )}
            </Box>
            <TextField
              type="datetime-local"
              value={lessonDetailsValues?.vaas_creation_timestamp || ""}
              onChange={(e) => {
                setLessonDetailsValues((oldState) => ({
                  ...oldState,
                  vaas_creation_timestamp: e.target.value,
                }))
              }}
              style={{ marginBottom: "10px" }}
              InputProps={{
                classes: {
                  root: classes.inputBackgroundColor,
                },
              }}
              InputLabelProps={{ shrink: true }}
              variant="filled"
              size="small"
              fullWidth
            />
          </Box>
        </>
      )}
    </Box>
  )
}

LessonSettings.propTypes = {}

export default LessonSettings
